<template>
  <div>
    <div class="mb-8 mt-24 flex items-center justify-end">
      <RevButton
        v-if="!shouldShowDeleteConfirmationMessage"
        :disabled="!hasSelectedFiles"
        size="small"
        variant="secondaryDestructive"
        @click="handleDeleteSelectedFiles"
      >
        {{ i18n(translations.deleteSelectedFilesButton) }}
      </RevButton>

      <div
        v-if="shouldShowDeleteConfirmationMessage"
        class="flex items-center space-x-12"
      >
        <span>
          {{
            i18n(translations.confirmDeleteMessage, {
              count: selectedFilesIds.length,
            })
          }}
        </span>

        <RevButton
          :loading="isDeleting"
          size="small"
          variant="primary"
          @click="handleConfirm"
        >
          {{
            i18n(translations.yesDeleteMessage, {
              count: selectedFilesIds.length,
            })
          }}
        </RevButton>

        <RevButton size="small" variant="secondary" @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevButton>
      </div>
    </div>

    <div v-if="apiErrorMessage" class="mb-24 flex justify-center">
      <RevInfoBlock
        :icon="IconWarning"
        :title="i18n(translations.errorTitle)"
        variant="danger"
      >
        {{ apiErrorMessage }}
      </RevInfoBlock>
    </div>

    <RevTable
      v-model:checked-ids="selectedFilesIds"
      :collection="fileList"
      :columns="fileColumns"
    >
      <template #body-time="{ item }">
        <div class="whitespace-nowrap">
          {{ i18n.date(new Date(item.creationDate), DATE_TIME_MINUTES_FORMAT) }}
        </div>
      </template>
      <template #body-type="{ item }">
        <RevTag
          :label="item.type.label"
          :variant="getTypeTagColor(item.type.code)"
        />
      </template>
      <template #body-status="{ item }">
        <div class="flex items-center">
          <IconCheckInCircle
            v-if="item.status.code === 'SUCCESS'"
            class="mr-8 shrink-0"
            color="green"
          />
          <IconCrossInCircle
            v-if="item.status.code === 'FAILED'"
            class="mr-8 shrink-0"
            color="red"
          />
          <IconInfo
            v-if="item.status.code === 'WARNING'"
            class="mr-8 shrink-0"
          />
          <div>
            <div v-for="(message, index) in item.messages" :key="index">
              {{ message }}
            </div>
            <RevLink
              v-if="item.errorLink"
              class="block"
              target="_blank"
              :to="item.errorLink.href"
            >
              {{ i18n(translations.seeErrorReport) }}
            </RevLink>
          </div>
        </div>
      </template>
      <template #body-download="{ item }">
        <RevButtonIcon
          v-if="getFileDownloadLink(item)"
          :aria-label="i18n(translations.downloadButton)"
          :icon="IconDownload"
          variant="secondary"
          @click="downloadFile(getFileDownloadLink(item))"
        />
      </template>
    </RevTable>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { type Url, merchantsAPI } from '@backmarket/http-api'
import type { SellerFile } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/files.d'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconWarning } from '@ds/icons/IconWarning'

import { DATE_TIME_MINUTES_FORMAT } from '~/constants/dateTimeFormats'

import translations from './TheFileLibrary.translations'

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const { deleteSellerFiles } = merchantsAPI

defineProps<{ fileList: SellerFile[] }>()
const emit = defineEmits(['seller-file-deleted'])

const apiErrorMessage = ref('')
const isDeleting = ref(false)
const selectedFilesIds = ref<string[]>([])
const shouldShowDeleteConfirmationMessage = ref(false)

const fileColumns = [
  {
    key: 'time',
    label: i18n(translations.time),
  },
  {
    key: 'type',
    label: i18n(translations.type),
  },
  {
    key: 'title',
    label: '',
  },
  {
    key: 'operationId',
    label: i18n(translations.id),
  },
  {
    key: 'status',
    label: '',
  },
  {
    key: 'download',
    label: '',
  },
]

const getTypeTagColor = (type: string) =>
  type === 'EXPORT' ? 'alternate' : 'warning'

const getFileDownloadLink = (file: SellerFile) => {
  if (file.fileInLink?.href) {
    return file.fileInLink.href
  }
  if (file.fileOutLink?.href) {
    return file.fileOutLink.href
  }

  return null
}

const downloadFile = (fileDownloadLink: Url | null) => {
  if (fileDownloadLink) window.open(fileDownloadLink, '_blank')
}

const handleCancel = () => {
  selectedFilesIds.value = []
  apiErrorMessage.value = ''
  shouldShowDeleteConfirmationMessage.value = false
}

const hasSelectedFiles = computed(() => !isEmpty(selectedFilesIds.value))

const handleDeleteSelectedFiles = () => {
  shouldShowDeleteConfirmationMessage.value = true
}

const handleConfirm = async () => {
  isDeleting.value = true

  const { error: fileDeleteError } = await useHttpFetch(deleteSellerFiles, {
    body: {
      ids: selectedFilesIds.value,
    },
  })

  if (fileDeleteError.value) {
    openErrorToast({
      // TODO : improve after FSM office hours
      content: getApiErrorMessage(fileDeleteError.value as unknown as Error),
    })
  } else {
    emit('seller-file-deleted')
  }
  isDeleting.value = false
}
</script>
